import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Text,
  Flex,
  Input,
  Button,
  useToast,
  Spinner,
  IconButton,
  Textarea,
  Switch,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
} from '@chakra-ui/react';
import { ChevronRightIcon, ChevronLeftIcon, DeleteIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import { fetchCurrencies, updateCurrency, createCurrency } from '../../api/endpointsBackend';
import CurrencyAvatar from './CurrencyAvatar';

const STATUS_PILL_PROPS = {
  textStyle: 'microLabel',
  px: 2,
  py: '2px',
  borderRadius: 'sm',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
};

function StatusPill({ isActive }) {
  const { t } = useTranslation('currencies');
  return (
    <Box
      {...STATUS_PILL_PROPS}
      bg={isActive ? 'rgba(21, 128, 61, 0.10)' : 'redesign.bg.card'}
      color={isActive ? 'redesign.amount.income' : 'redesign.text.muted'}
      _dark={isActive ? { bg: 'rgba(74, 222, 128, 0.14)' } : undefined}
    >
      <Box w="6px" h="6px" borderRadius="full" bg="redesign.accent.green" opacity={isActive ? 1 : 0.4} />
      {isActive ? t('detail.status.active') : t('detail.status.inactive')}
    </Box>
  );
}

function StandardPill() {
  const { t } = useTranslation('currencies');
  return (
    <Box
      {...STATUS_PILL_PROPS}
      bg="redesign.bg.card"
      color="redesign.text.muted"
    >
      {t('standard')}
    </Box>
  );
}

export default function CurrencyDetailPanel({ currencyId, onClose, onCurrencyUpdated, onDelete }) {
  const { t } = useTranslation('currencies');
  const [currency, setCurrency] = useState(null);
  const [editedCurrency, setEditedCurrency] = useState(null);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [isEditingCode, setIsEditingCode] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [pegCurrencies, setPegCurrencies] = useState([]);
  const toast = useToast();

  const isCreateMode = !currencyId;
  const isCustomCurrency = !isCreateMode && currency?.type !== 'standard';
  const canEdit = isCreateMode || isCustomCurrency;

  const fetchCurrencyData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const currencies = await fetchCurrencies();
      const found = currencies.find(c => c.id === currencyId);
      if (found) {
        setCurrency(found);
        setEditedCurrency(found);
      } else {
        setError(t('detail.notFound'));
      }
    } catch (err) {
      console.error('Error fetching currency:', err);
      setError(t('detail.loadError'));
      toast({ title: t('detail.toasts.errorTitle'), description: t('detail.loadError'), status: 'error', duration: 5000, isClosable: true });
    } finally {
      setLoading(false);
    }
  }, [currencyId, toast, t]);

  // MI-298: active currencies a custom currency can be pegged to.
  useEffect(() => {
    fetchCurrencies(true)
      .then((list) => setPegCurrencies(list || []))
      .catch(() => setPegCurrencies([]));
  }, []);

  useEffect(() => {
    setShowEditForm(false);
    if (currencyId) {
      fetchCurrencyData();
    } else {
      const empty = { name: '', description: '', is_active: true };
      setCurrency(empty);
      setEditedCurrency(empty);
      setIsEditingCode(true);
      setShowEditForm(true);
    }
  }, [currencyId, fetchCurrencyData]);

  const handleSave = async () => {
    setUpdating(true);
    try {
      const payload = {
        name: editedCurrency.name,
        description: editedCurrency.description || '',
        is_active: editedCurrency.is_active,
        // MI-298: manual peg — only custom currencies reach this form.
        manual_rate: editedCurrency.manual_rate ?? null,
        manual_rate_currency: editedCurrency.manual_rate_currency || null,
      };
      if (isCreateMode) {
        await createCurrency(payload);
      } else {
        await updateCurrency(currencyId, payload);
      }
      toast({
        title: t('detail.toasts.successTitle'),
        description: isCreateMode ? t('detail.toasts.createSuccess') : t('detail.toasts.updateSuccess'),
        status: 'success', duration: 3000, isClosable: true,
      });
      if (onCurrencyUpdated) onCurrencyUpdated();
      if (isCreateMode && onClose) onClose();
      // Re-read from the server instead of trusting the local copy: the panel
      // otherwise keeps rendering whatever it held before the save, so a value
      // the backend cleared can still look set. Cheap, and it makes what's on
      // screen after a save mean "this is what was stored".
      if (!isCreateMode) await fetchCurrencyData();
      setShowEditForm(false);
    } catch (err) {
      console.error(`Error ${isCreateMode ? 'creating' : 'updating'} currency:`, err);
      toast({
        title: t('detail.toasts.errorTitle'),
        description: err.response?.data?.detail || t('detail.toasts.saveFailed'),
        status: 'error', duration: 5000, isClosable: true,
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setEditedCurrency(prev => ({ ...prev, [field]: value }));
  };

  const handleDeleteClick = () => {
    if (onDelete && currency) {
      onClose();
      onDelete(currency);
    }
  };

  const renderEditForm = () => {
    if (!editedCurrency) return null;
    return (
    <Box px={{ base: 2, md: 4 }} py={4}>
      {/* Code (acts as the name) + status pill */}
      <Box mb={4}>
        {isEditingCode ? (
          <Input
            value={editedCurrency.name}
            onChange={(e) => handleFieldChange('name', e.target.value)}
            onBlur={() => setIsEditingCode(false)}
            autoFocus
            fontSize="xl"
            fontWeight="600"
            variant="unstyled"
            placeholder={t('detail.fields.code')}
          />
        ) : (
          <Text
            fontSize="xl"
            fontWeight="600"
            onClick={() => setIsEditingCode(true)}
            cursor="pointer"
            _hover={{ color: 'redesign.accent.blue' }}
          >
            {editedCurrency.name || t('detail.fields.addCode')}
          </Text>
        )}
        {!isCreateMode && (
          <Popover placement="bottom-start">
            {({ onClose: closeStatusPopover }) => (
              <>
                <PopoverTrigger>
                  <Flex align="center" gap={1} cursor="pointer" _hover={{ opacity: 0.8 }} mt={1}>
                    <StatusPill isActive={!!editedCurrency.is_active} />
                    <ChevronRightIcon color="redesign.text.muted" boxSize={3} />
                  </Flex>
                </PopoverTrigger>
                <PopoverContent w="150px" borderRadius="lg">
                  <PopoverBody p={1}>
                    {[true, false].map(val => (
                      <Flex
                        key={String(val)}
                        px={3} py={2}
                        cursor="pointer"
                        borderRadius="md"
                        bg={val === editedCurrency.is_active ? 'redesign.bg.accent' : 'transparent'}
                        _hover={{ bg: 'redesign.bg.hover' }}
                        onClick={() => { handleFieldChange('is_active', val); closeStatusPopover(); }}
                      >
                        <Text fontSize="sm" fontWeight={val === editedCurrency.is_active ? '600' : '400'} color={val ? 'redesign.amount.income' : 'redesign.text.muted'}>
                          {val ? t('detail.status.active') : t('detail.status.inactive')}
                        </Text>
                      </Flex>
                    ))}
                  </PopoverBody>
                </PopoverContent>
              </>
            )}
          </Popover>
        )}
      </Box>

      {/* Description */}
      <Box py={3} borderBottom="1px solid" borderColor="redesign.border">
        <Text fontSize="sm" color="redesign.text.muted" mb={1}>{t('detail.fields.description')}</Text>
        {isEditingDescription ? (
          <Textarea
            value={editedCurrency.description || ''}
            onChange={(e) => handleFieldChange('description', e.target.value)}
            onBlur={() => setIsEditingDescription(false)}
            autoFocus
            placeholder={t('detail.fields.descriptionPlaceholder')}
            size="sm"
            rows={3}
            fontSize="sm"
          />
        ) : (
          <Text
            fontSize="sm"
            color="redesign.text"
            whiteSpace="pre-wrap"
            onClick={() => setIsEditingDescription(true)}
            cursor="pointer"
            _hover={{ color: 'redesign.accent.blue' }}
            minH="32px"
          >
            {editedCurrency.description || t('detail.fields.addDescription')}
          </Text>
        )}
      </Box>

      {/* MI-298: manual peg so this custom currency can be converted */}
      <Box py={3} borderBottom="1px solid" borderColor="redesign.border">
        <Text fontSize="sm" color="redesign.text.muted" mb={1}>{t('detail.fields.peg')}</Text>
        <Flex align="center" gap={2} wrap="wrap">
          <Text fontSize="sm" color="redesign.text" whiteSpace="nowrap">
            1 {editedCurrency.name || t('detail.fields.pegThisCurrency')} =
          </Text>
          <Input
            type="number"
            value={editedCurrency.manual_rate ?? ''}
            onChange={(e) => handleFieldChange('manual_rate', e.target.value === '' ? null : parseFloat(e.target.value))}
            placeholder={t('detail.fields.pegRatePlaceholder')}
            size="sm"
            w="120px"
          />
          <Popover placement="bottom-start">
            {({ onClose: closePegPopover }) => (
              <>
                <PopoverTrigger>
                  <Flex
                    align="center" gap={1}
                    px={3} py={1.5}
                    borderRadius="md"
                    border="1px solid"
                    borderColor="redesign.border.strong"
                    cursor="pointer"
                    _hover={{ bg: 'redesign.bg.hover' }}
                  >
                    <Text fontSize="sm" color={editedCurrency.manual_rate_currency ? 'redesign.text' : 'redesign.text.muted'}>
                      {editedCurrency.manual_rate_currency || t('detail.fields.pegSelectCurrency')}
                    </Text>
                    <ChevronRightIcon color="redesign.text.muted" boxSize={3} />
                  </Flex>
                </PopoverTrigger>
                <PopoverContent w="180px" borderRadius="lg">
                  <PopoverBody p={1} maxH="240px" overflowY="auto">
                    {/* Clearing the peg drops BOTH fields: a rate with no target
                        (or a target with no rate) converts nothing, so leaving
                        half of it behind would just look set without working. */}
                    <Flex
                      px={3} py={2}
                      cursor="pointer"
                      borderRadius="md"
                      bg={!editedCurrency.manual_rate_currency ? 'redesign.bg.accent' : 'transparent'}
                      _hover={{ bg: 'redesign.bg.hover' }}
                      borderBottom="1px solid"
                      borderColor="redesign.border"
                      onClick={() => {
                        setEditedCurrency(prev => ({
                          ...prev, manual_rate: null, manual_rate_currency: null,
                        }));
                        closePegPopover();
                      }}
                    >
                      <Text fontSize="sm" color="redesign.text.muted">
                        {t('detail.fields.pegNone')}
                      </Text>
                    </Flex>
                    {pegCurrencies
                      .filter((c) => c.name !== editedCurrency.name)
                      .map((c) => (
                        <Flex
                          key={c.id ?? c.name}
                          px={3} py={2}
                          cursor="pointer"
                          borderRadius="md"
                          bg={c.name === editedCurrency.manual_rate_currency ? 'redesign.bg.accent' : 'transparent'}
                          _hover={{ bg: 'redesign.bg.hover' }}
                          onClick={() => { handleFieldChange('manual_rate_currency', c.name); closePegPopover(); }}
                        >
                          <Text fontSize="sm" fontWeight={c.name === editedCurrency.manual_rate_currency ? '600' : '400'}>
                            {c.name}
                          </Text>
                        </Flex>
                      ))}
                  </PopoverBody>
                </PopoverContent>
              </>
            )}
          </Popover>
        </Flex>
        <Text fontSize="xs" color="redesign.text.muted" mt={1.5}>{t('detail.fields.pegHelp')}</Text>
      </Box>

      <Flex mt={4} gap={2}>
        <Button
          variant="primary"
          flex={1}
          size="sm"
          onClick={handleSave}
          isLoading={updating}
          isDisabled={!editedCurrency?.name?.trim()}
        >
          {isCreateMode ? t('detail.buttons.create') : t('detail.buttons.save')}
        </Button>
        {!isCreateMode && (
          <Button variant="outline" size="sm" onClick={() => { setShowEditForm(false); setEditedCurrency(currency); }}>
            {t('detail.buttons.cancel')}
          </Button>
        )}
      </Flex>
    </Box>
    );
  };

  const renderViewMode = () => (
    <Box flex={1} overflowY="auto" overflowX="hidden">
      <Box px={{ base: 3, md: 5 }} pt={5} pb={4}>
        <Flex align="center" gap={3} mb={3}>
          <CurrencyAvatar name={currency?.name || '?'} size={52} isInactive={!currency?.is_active} />
          <Flex gap={2} align="center">
            <StatusPill isActive={!!currency?.is_active} />
            {currency?.type === 'standard' && <StandardPill />}
          </Flex>
        </Flex>

        {/* Status toggle for standard currencies (the only thing they can change) */}
        {!canEdit && (
          <Flex py={2} align="center" borderTop="1px solid" borderColor="redesign.border" mt={2}>
            <Text fontSize="sm" color="redesign.text.muted" w="100px">{t('detail.fields.statusLabel')}</Text>
            <Switch
              size="md"
              isChecked={!!currency?.is_active}
              onChange={async () => {
                try {
                  await updateCurrency(currencyId, { is_active: !currency.is_active });
                  toast({
                    description: currency.is_active
                      ? t('toasts.deactivated', { name: currency.name })
                      : t('toasts.activated', { name: currency.name }),
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                  });
                  if (onCurrencyUpdated) onCurrencyUpdated();
                  fetchCurrencyData();
                } catch {
                  toast({ title: t('toasts.errorTitle'), description: t('toasts.updateFailed'), status: 'error', duration: 5000, isClosable: true });
                }
              }}
              aria-label={t('toggleAria', { name: currency?.name || '' })}
              sx={{ 'span.chakra-switch__track[data-checked]': { bg: 'redesign.heading' } }}
            />
          </Flex>
        )}

        {/* MI-298: configured manual peg (custom currencies) */}
        {isCustomCurrency && currency?.manual_rate != null && currency?.manual_rate_currency && (
          <Box py={3} borderTop="1px solid" borderColor="redesign.border" mt={2}>
            <Text fontSize="xs" color="redesign.text.muted" mb={1}>{t('detail.fields.peg')}</Text>
            <Text fontSize="sm" color="redesign.text">
              1 {currency.name} = {currency.manual_rate.toLocaleString('en-US')} {currency.manual_rate_currency}
            </Text>
          </Box>
        )}

        {currency?.description && (
          <Box py={3} borderTop="1px solid" borderColor="redesign.border" mt={2}>
            <Text fontSize="xs" color="redesign.text.muted" mb={1}>{t('detail.fields.description')}</Text>
            <Text fontSize="sm" color="redesign.text" whiteSpace="pre-wrap">
              {currency.description}
            </Text>
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <Box h="100%" bg="redesign.bg.surface" display="flex" flexDirection="column" position="relative">
      {/* Header */}
      <Flex
        justify="space-between"
        align="center"
        px={{ base: 3, md: 5 }}
        py={3}
        borderBottom="1px solid"
        borderColor="redesign.border"
        flexShrink={0}
      >
        <Flex gap={2} align="center">
          <IconButton icon={<ChevronLeftIcon boxSize={5} />} size="sm" variant="ghost" onClick={onClose} aria-label={t('detail.back')} />
          <Text fontSize="sm" fontWeight="500" color="redesign.text">
            {isCreateMode ? t('detail.headerNew') : t('detail.header')}
          </Text>
        </Flex>
        <Flex gap={2} align="center">
          {!isCreateMode && !showEditForm && canEdit && (
            <Button
              size="xs"
              variant="outline"
              onClick={() => { setShowEditForm(true); setEditedCurrency(currency); }}
            >
              {t('detail.edit')}
            </Button>
          )}
          {isCustomCurrency && (
            <Menu>
              <MenuButton
                as={IconButton}
                icon={<Text fontSize="md">⋯</Text>}
                variant="ghost"
                size="sm"
                aria-label={t('detail.options')}
              />
              <MenuList>
                <MenuItem icon={<DeleteIcon />} color="redesign.accent.red" onClick={handleDeleteClick}>
                  {t('detail.delete')}
                </MenuItem>
              </MenuList>
            </Menu>
          )}
        </Flex>
      </Flex>

      {/* Content */}
      {loading ? (
        <Flex justify="center" align="center" flex={1}>
          <Spinner size="xl" />
        </Flex>
      ) : error ? (
        <Text color="redesign.accent.red" textAlign="center" py={12}>{error}</Text>
      ) : showEditForm || isCreateMode ? (
        <Box flex={1} overflowY="auto" overflowX="hidden">
          {renderEditForm()}
        </Box>
      ) : (
        renderViewMode()
      )}
    </Box>
  );
}
